import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const CATEGORY_OPTIONS = {
  women: [
    { value: 'rings', label: 'Rings' },
    { value: 'earrings', label: 'Earrings' },
    { value: 'necklace', label: 'Necklace' },
    { value: 'kada-bracelet', label: 'Kada Bracelet' },
    { value: 'chain-bracelet', label: 'Chain Bracelet' },
    { value: 'bangles', label: 'Bangles' },
    { value: 'anklets', label: 'Anklets' },
    { value: 'watches', label: 'Watches' },
    { value: 'handchain', label: 'Handchain' },
  ],
  men: [
    { value: 'chains', label: 'Chains' },
    { value: 'kada-bracelet', label: 'Kada Bracelet' },
    { value: 'chain-bracelet', label: 'Chain Bracelet' },
    { value: 'rings', label: 'Rings' },
  ],
  unisex: [
    { value: 'rings', label: 'Rings' },
    { value: 'chains', label: 'Chains' },
    { value: 'kada-bracelet', label: 'Kada Bracelet' },
    { value: 'chain-bracelet', label: 'Chain Bracelet' },
    { value: 'watches', label: 'Watches' },
  ],
};

const SECTIONS = ['women', 'men', 'unisex'];

export default function AdminAddProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'rings',
    section: 'women',
    price: '',
    discountPrice: '',
    stock: '',
    metal: '',
    stone: '',
    weight: '',
    isFeatured: false,
    isNewArrival: false,
    tags: '',
  });

  useEffect(() => {
    if (isEdit) {
      API.get(`/products/${id}`)
        .then((r) => {
          const p = r.data;

          setForm({
            name: p.name || '',
            description: p.description || '',
            category: p.category || 'rings',
            section: p.section || 'women',
            price: p.price || '',
            discountPrice: p.discountPrice || '',
            stock: p.stock || '',
            metal: p.materialDetails?.metal || '',
            stone: p.materialDetails?.stone || '',
            weight: p.materialDetails?.weight || '',
            isFeatured: !!p.isFeatured,
            isNewArrival: !!p.isNewArrival,
            tags: p.tags?.join(',') || '',
          });

          setImages(p.images || []);
        })
        .catch(() => toast.error('Failed to load product'));
    }
  }, [id, isEdit]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSectionChange = (section) => {
    updateField('section', section);
    updateField('category', CATEGORY_OPTIONS[section][0].value);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    const data = new FormData();
    files.forEach((file) => data.append('images', file));

    try {
      setUploading(true);

      const res = await API.post('/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setImages((prev) => [...prev, ...(res.data.images || [])]);
      toast.success('Images uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Product name is required');
    if (!form.price) return toast.error('Price is required');
    if (!form.description.trim()) return toast.error('Description is required');

    if (!images || images.length < 1) {
      return toast.error('Please upload at least 1 product image');
    }

    if (images.length > 5) {
      return toast.error('Maximum 5 product images allowed');
    }

    setSaving(true);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      section: form.section,
      price: Number(form.price),
      discountPrice: Number(form.discountPrice) || undefined,
      stock: Number(form.stock) || 0,
      materialDetails: {
        metal: form.metal,
        stone: form.stone,
        weight: form.weight,
      },
      isFeatured: form.isFeatured,
      isNewArrival: form.isNewArrival,
      tags: form.tags
        ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      images,
    };

    try {
      if (isEdit) {
        await API.put(`/products/${id}`, payload);
        toast.success('Product updated');
      } else {
        await API.post('/products', payload);
        toast.success('Product added');
      }

      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title={isEdit ? 'Edit Product' : 'Add New Product'}>
      <div
        style={{
          background: 'white',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 32,
          maxWidth: 820,
        }}
      >
        <div className="form-group">
          <label className="form-label">Product Name *</label>
          <input
            className="form-input"
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g. Premium Stainless Steel Ring"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Section</label>
            <select
              className="form-input"
              value={form.section}
              onChange={(e) => handleSectionChange(e.target.value)}
            >
              {SECTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <select
              className="form-input"
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
            >
              {CATEGORY_OPTIONS[form.section].map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Stock Quantity</label>
            <input
              className="form-input"
              type="number"
              value={form.stock}
              onChange={(e) => updateField('stock', e.target.value)}
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Price ₹ *</label>
            <input
              className="form-input"
              type="number"
              value={form.price}
              onChange={(e) => updateField('price', e.target.value)}
              placeholder="999"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Discount Price ₹</label>
          <input
            className="form-input"
            type="number"
            value={form.discountPrice}
            onChange={(e) => updateField('discountPrice', e.target.value)}
            placeholder="799"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description *</label>
          <textarea
            className="form-input"
            rows={4}
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Describe the product..."
          />
        </div>

        <h4 style={{ fontSize: 20, margin: '14px 0 16px' }}>
          Material Details
        </h4>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Metal</label>
            <input
              className="form-input"
              value={form.metal}
              onChange={(e) => updateField('metal', e.target.value)}
              placeholder="Premium Stainless Steel"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Stone / Gemstone</label>
            <input
              className="form-input"
              value={form.stone}
              onChange={(e) => updateField('stone', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Weight</label>
          <input
            className="form-input"
            value={form.weight}
            onChange={(e) => updateField('weight', e.target.value)}
            placeholder="4.2g"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Product Images 1 to 5 Required</label>

          <label
            style={{
              border: '1px dashed var(--champagne)',
              background: 'var(--cream)',
              borderRadius: 8,
              padding: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 10,
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            <Upload size={26} color="var(--maroon)" />
            <strong style={{ color: 'var(--maroon)' }}>
              {uploading ? 'Uploading...' : 'Upload Images from Device'}
            </strong>
            <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
              Minimum 1 image required. Maximum 5 images allowed.
            </span>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>

          {images.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))',
                gap: 12,
                marginTop: 14,
              }}
            >
              {images.map((img, index) => (
                <div
                  key={`${img.url}-${index}`}
                  style={{
                    position: 'relative',
                    height: 110,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--border)',
                    background: 'var(--cream)',
                  }}
                >
                  <img
                    src={img.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      border: 'none',
                      background: 'rgba(107,27,42,0.9)',
                      color: 'white',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Tags comma separated</label>
          <input
            className="form-input"
            value={form.tags}
            onChange={(e) => updateField('tags', e.target.value)}
            placeholder="bestseller, new, men, women"
          />
        </div>

        <div style={{ display: 'flex', gap: 24, margin: '8px 0 24px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={form.isFeatured}
              onChange={(e) => updateField('isFeatured', e.target.checked)}
            />
            Featured on Homepage
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={form.isNewArrival}
              onChange={(e) => updateField('isNewArrival', e.target.checked)}
            />
            New Arrival
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="btn btn-primary btn-lg"
            onClick={save}
            disabled={saving || uploading}
            style={{ flex: 1 }}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Product' : 'Add Product'}
          </button>

          <button
            className="btn btn-outline btn-lg"
            onClick={() => navigate('/admin/products')}
          >
            Cancel
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}